import { InputGroup } from "@blueprintjs/core";
import { Field, FieldProps, useFormikContext } from "formik";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { PlanrPopover } from "modules/common/components/planr/popover/Popover";
import { getSiteAddress } from "modules/common/services/communication/http";
import { CredentialsActions } from "./CredentialsActions";
import { useCallback, useEffect, useState } from "react";
import { AccessStatus } from "./access-link-actions";
import { generatePassword } from "./password";
import { useMst } from "modules/root/models/hooks";
import { can } from "modules/session/auth/access";
import func from "modules/session/functionalities";
import { BlockAccessDialog } from "./BlockAccessDialog";
import styles from "./AccessBlock.module.scss";

export interface Credentials {
    login: string;
    password: string;
}

interface AccessBlockProps {
    /** Поле логина в форме карточки */
    loginField: string;
    /** Поле нового пароля в форме: туда кладётся пароль, пока карточка не сохранена */
    passwordField: string;
    /** Чья карточка */
    accountId: string;
    /** Учётной записи ещё нет (новая карточка, заявка агента) */
    isNew: boolean;
    canChange: boolean;
    load: () => Promise<AccessStatus | null>;
    send: () => Promise<AccessStatus | null>;
    revoke: () => Promise<AccessStatus | null>;
    /** Новый пароль сохранённой учётной записи: старый сразу перестаёт работать */
    generate: () => Promise<Credentials | null>;
    /** Можно ли отправлять ссылку (у заявки агента - нет: приглашение уйдёт при одобрении) */
    canInvite?: boolean;
    /** Дополнительный класс блока (например, ширина в конкретной карточке) */
    className?: string;
    /** Отключить / включить доступ к приложению (право «Настройки приложения») */
    block: (reason: string) => Promise<AccessStatus | null>;
    unblock: () => Promise<AccessStatus | null>;
    /** Доступ отключили или включили: список рядом перечитывается, чтобы ФИО сменило цвет */
    onAccessChanged?: () => void;
    /** Отправить только что сгенерированные данные для входа на почту */
    mailCredentials: (password: string) => Promise<boolean>;
    /**
     * Письма из несохранённой карточки: поля формы-флаги, по которым сервер после
     * сохранения отправит данные для входа или приглашение, и поле почты для проверки
     */
    afterSave?: { mailField: string; inviteField: string; emailField: string };
    /** Состояние входа загрузилось или поменялось - например, для отметки на кнопке, открывающей блок */
    onStatus?: (status: AccessStatus | null) => void;
}

type PopoverState = { kind: "confirm" } | { kind: "result"; credentials: Credentials; pending: boolean } | null;

/**
 * Доступ в карточке сотрудника или контрагента: логин, пароль, ссылка для входа.
 * Пароль не вводится руками - его генерирует система и показывает один раз. У новой
 * карточки (или нового логина) пароль генерируется сразу и сохраняется вместе с карточкой.
 */
export const AccessBlock = ({
    loginField,
    passwordField,
    accountId,
    isNew,
    canChange,
    load,
    send,
    revoke,
    generate,
    canInvite = true,
    className,
    block,
    unblock,
    onAccessChanged,
    mailCredentials,
    afterSave,
    onStatus,
}: AccessBlockProps) => {
    const { session, notify } = useMst();
    // отключать доступ может тот, кто правит настройки приложения
    const canBlock = can(func.SETTINGS_EDIT, session.access);
    const [blocking, setBlocking] = useState(false);
    const form = useFormikContext<Record<string, any>>();
    const formLogin = String(form.values[loginField] ?? "").trim();
    const pendingPassword = String(form.values[passwordField] ?? "");

    const [status, setStatus] = useState<AccessStatus | null>(null);
    const [busy, setBusy] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [popover, setPopover] = useState<PopoverState>(null);
    const [copied, setCopied] = useState(false);
    // время, когда пароль сохранённой учётной записи сменили из этой карточки
    const [changedAt, setChangedAt] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setStatus(null);
        setChangedAt(null);

        if (!isNew && accountId) {
            load().then((result) => alive && setStatus(result));
        }

        return () => {
            alive = false;
        };
        // load меняется вместе с карточкой; перечитываем по id
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, isNew]);

    useEffect(() => {
        if (status) {
            onStatus?.(status);
        }
        // onStatus - колбэк родителя, следим только за самим состоянием
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    // поправили логин - прежняя ошибка уже не про него
    useEffect(() => setLoginError(""), [formLogin]);

    const run = useCallback(async (action: () => Promise<AccessStatus | null>) => {
        setBusy(true);
        const result = await action();
        setBusy(false);

        if (result) {
            setStatus(result);
        }
    }, []);

    // логин ещё не записан в учётную запись: пароль уйдёт вместе с карточкой
    const unsaved = isNew || !status || formLogin !== (status.login ?? "");
    const hasPassword = !!status?.hasPassword;

    const showResult = (credentials: Credentials, pending: boolean) => {
        setPopover({ kind: "result", credentials, pending });
    };

    const formEmail = afterSave ? String(form.values[afterSave.emailField] ?? "").trim() : "";
    const mailAfterSave = !!afterSave && !!form.values[afterSave.mailField];
    const inviteAfterSave = !!afterSave && !!form.values[afterSave.inviteField];

    /** Письмо из несохранённой карточки: проверяем почту и ставим флаг, уйдёт после сохранения */
    const scheduleAfterSave = (what: "mail" | "invite") => {
        if (!afterSave) {
            return;
        }

        if (!formEmail) {
            notify.error("Укажите почту в карточке — письмо некуда отправить");
            return;
        }

        form.setFieldValue(afterSave.mailField, what === "mail");
        form.setFieldValue(afterSave.inviteField, what === "invite");
        notify.success(
            what === "mail"
                ? `Данные для входа отправятся на ${formEmail} после сохранения карточки`
                : `Приглашение отправится на ${formEmail} после сохранения карточки`
        );
    };

    const cancelAfterSave = () => {
        if (afterSave) {
            form.setFieldValue(afterSave.mailField, false);
            form.setFieldValue(afterSave.inviteField, false);
        }
    };

    const doGenerate = async () => {
        if (unsaved) {
            const password = generatePassword();
            form.setFieldValue(passwordField, password);
            // пароль задан - приглашение уже не нужно. Отправку данных на почту оставляем:
            // сервер берёт пароль при сохранении, и в письмо уйдёт этот, последний
            if (afterSave) {
                form.setFieldValue(afterSave.inviteField, false);
            }
            showResult({ login: formLogin, password }, true);
            notify.success("Пароль сгенерирован — он заработает после сохранения карточки");
            return;
        }

        setBusy(true);
        const credentials = await generate();
        setBusy(false);

        if (credentials) {
            showResult(credentials, false);
            notify.success("Пароль изменён — старый больше не работает");
            setChangedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
            run(load);
        } else {
            setPopover(null);
        }
    };

    const onGenerateClick = () => {
        if (!formLogin) {
            setLoginError("Укажите логин");
            return;
        }

        // сохранённый пароль заменяется сразу - спросим; несохранённый - нечего терять
        if (!unsaved && hasPassword) {
            setPopover({ kind: "confirm" });
        } else {
            doGenerate();
        }
    };


    useEffect(() => {
        if (!copied) {
            return;
        }

        const timer = setTimeout(() => setCopied(false), 3000);
        return () => clearTimeout(timer);
    }, [copied]);

    const hasEmail = !!status?.email?.trim();
    const link = status?.link ?? null;
    const blocked = status?.blocked ?? null;

    const closeButton = (
        <button type="button" className={styles.close} title="Закрыть" onClick={() => setPopover(null)}>
            <GeneralIcon type="general-cross-small" size={14} />
        </button>
    );

    const popoverContent =
        popover?.kind === "confirm" ? (
            <div className={styles.popover}>
                <div className={styles.popoverHead}>
                    <span className={styles.popoverTitle}>Новый пароль?</span>
                    {closeButton}
                </div>
                <div>Текущий пароль сразу перестанет работать.</div>
                <div className={styles.popoverActions}>
                    <PlanrButton
                        type="blueish"
                        size="small"
                        icon="general-shuffle"
                        onClick={doGenerate}
                        disabled={busy}
                    >
                        Сгенерировать
                    </PlanrButton>
                </div>
            </div>
        ) : popover?.kind === "result" ? (
            <div className={styles.popover}>
                <div className={styles.popoverHead}>
                    <span className={styles.popoverTitle}>Данные для входа</span>
                    {closeButton}
                </div>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Адрес</span>
                    <span className={styles.value}>{getSiteAddress()}</span>
                </div>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Логин</span>
                    <span className={styles.value}>{popover.credentials.login}</span>
                </div>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Пароль</span>
                    <span className={styles.value}>{popover.credentials.password}</span>
                </div>
                <div className={styles.note}>
                    {popover.pending
                        ? "Данные заработают после сохранения карточки. Скопируйте их сейчас — пароль больше не покажется."
                        : "Пароль уже изменён, старый больше не работает. Скопируйте данные сейчас — пароль больше не покажется."}
                </div>
                <CredentialsActions
                    credentials={popover.credentials}
                    name={String(form.values.name ?? "").trim() || undefined}
                    onCopied={() => {
                        setPopover(null);
                        setCopied(true);
                    }}
                    onMail={async () =>
                        popover.pending
                            ? scheduleAfterSave("mail")
                            : mailCredentials(popover.credentials.password)
                    }
                    mailDisabled={
                        popover.pending
                            ? afterSave
                                ? undefined
                                : "Сначала сохраните карточку — пароль ещё не записан"
                            : !hasEmail
                              ? "Почта не указана"
                              : undefined
                    }
                />
            </div>
        ) : (
            <span />
        );

    return (
        <div className={`${styles.block} ${className ?? ""}`}>
            <div className={styles.head}>
                <span className={styles.title}>Доступ</span>
                {blocked ? (
                    <span className={`${styles.status} ${styles.statusBlocked}`}>
                        <span className={styles.dot} />
                        доступ отключён
                    </span>
                ) : (
                    (status || pendingPassword) && (
                        <span
                            className={`${styles.status} ${
                                pendingPassword
                                    ? styles.statusPending
                                    : hasPassword
                                      ? styles.statusOn
                                      : styles.statusOff
                            }`}
                        >
                            <span className={styles.dot} />
                            {pendingPassword ? "сохраните карточку" : hasPassword ? "вход настроен" : "нет пароля"}
                        </span>
                    )
                )}
            </div>

            <div className={styles.label}>Логин</div>
            <Field name={loginField}>
                {({ field }: FieldProps) => (
                    <InputGroup
                        {...field}
                        value={field.value ?? ""}
                        id={loginField}
                        className={`planr-default-input ${styles.loginInput} ${loginError ? styles.inputError : ""}`}
                        autoComplete="off"
                        disabled={!canChange}
                        small
                    />
                )}
            </Field>
            {loginError && <div className={styles.error}>{loginError}</div>}

            {blocked && (
                <div className={styles.blocked}>
                    <div>
                        Отключён {blocked.at}
                        {blocked.by ? `, ${blocked.by}` : ""}
                    </div>
                    {blocked.reason && <div className={styles.blockedReason}>«{blocked.reason}»</div>}
                </div>
            )}

            {blocked && canBlock && (
                <div className={styles.actions}>
                    <PlanrButton
                        type="greenish"
                        size="small"
                        icon="general-check"
                        disabled={busy}
                        onClick={() => run(unblock).then(() => onAccessChanged?.())}
                    >
                        Включить доступ
                    </PlanrButton>
                </div>
            )}

            {canChange && !blocked && (
                <div className={styles.actions}>
                    <PlanrPopover
                        isOpen={popover !== null}
                        content={popoverContent}
                        placement="bottom-start"
                        usePortal={true}
                        onInteraction={(next) => !next && setPopover(null)}
                    >
                        <span className={styles.target}>
                            <PlanrButton
                                type="blueish"
                                size="small"
                                icon="general-shuffle"
                                disabled={busy}
                                onClick={onGenerateClick}
                                title="Новый пароль покажется один раз — его можно скопировать и передать"
                            >
                                Сгенерировать пароль
                            </PlanrButton>
                        </span>
                    </PlanrPopover>

                    {canInvite && !isNew && status && hasEmail && (
                        <PlanrButton
                            type="secondary"
                            size="small"
                            icon="general-mail"
                            disabled={busy}
                            onClick={() => run(send)}
                            title={
                                hasPassword
                                    ? `На ${status.email} уйдёт ссылка, по которой можно задать новый пароль`
                                    : `На ${status.email} уйдёт приглашение: человек сам задаст логин и пароль`
                            }
                        >
                            {hasPassword ? "Сброс по ссылке" : "Пригласить"}
                        </PlanrButton>
                    )}

                    {/* новая карточка: приглашение уйдёт после сохранения */}
                    {canInvite && isNew && afterSave && !pendingPassword && (
                        <PlanrButton
                            type="secondary"
                            size="small"
                            icon="general-mail"
                            disabled={inviteAfterSave}
                            onClick={() => scheduleAfterSave("invite")}
                            title="После сохранения карточки на почту уйдёт приглашение: человек сам задаст логин и пароль"
                        >
                            Пригласить
                        </PlanrButton>
                    )}
                </div>
            )}

            {(mailAfterSave || inviteAfterSave) && (
                <div className={styles.afterSave}>
                    <GeneralIcon type="general-mail" size={12} />
                    <span className={styles.afterSaveText}>
                        После сохранения на {formEmail || "почту"} уйдёт{" "}
                        {mailAfterSave ? "письмо с данными для входа" : "приглашение"}
                    </span>
                    <button type="button" className={styles.textButton} onClick={cancelAfterSave}>
                        отменить
                    </button>
                </div>
            )}

            {changedAt && !copied && (
                <div className={styles.changed}>
                    <GeneralIcon type="general-check" size={12} />
                    Пароль изменён в {changedAt}, старый больше не работает
                </div>
            )}

            {copied && (
                <div className={styles.copied}>
                    <GeneralIcon type="general-check" size={12} />
                    Данные для входа скопированы
                </div>
            )}

            {link && (
                <div
                    className={styles.link}
                    title={`${link.purpose === "invite" ? "Приглашение" : "Ссылка для смены пароля"} отправлено на ${
                        link.email
                    } ${link.created}${link.author ? `, отправил ${link.author.name}` : ", по запросу пользователя"}`}
                >
                    <GeneralIcon type="general-clock" size={12} />
                    <span className={styles.linkText}>
                        {link.purpose === "invite" ? "Приглашение" : "Ссылка"} до {link.expires}
                    </span>
                    {canChange && (
                        <button type="button" className={styles.textButton} disabled={busy} onClick={() => run(revoke)}>
                            отозвать
                        </button>
                    )}
                </div>
            )}

            {canChange && canInvite && !isNew && status && !hasEmail && !blocked && (
                <div className={styles.hint}>Чтобы отправить приглашение, укажите почту</div>
            )}

            {canBlock && !isNew && status && !blocked && (
                <button type="button" className={styles.blockLink} disabled={busy} onClick={() => setBlocking(true)}>
                    Отключить доступ
                </button>
            )}

            {blocking && (
                <BlockAccessDialog
                    who={`${String(form.values.name ?? "").trim() || status?.login || ""}${
                        status?.login ? ` (${status.login})` : ""
                    }`}
                    onConfirm={(reason) => run(() => block(reason)).then(() => onAccessChanged?.())}
                    onClose={() => setBlocking(false)}
                />
            )}
        </div>
    );
};
